import { useEffect, useRef } from 'react';

/**
 * Routes Escape and outside clicks through the caller instead of letting
 * `Overlay` close the drawer straight away — the user overlay has unsaved input
 * and wants to ask first.
 *
 * Attach the returned ref to an element inside the overlay and give that element
 * `data-overlay-handles-escape="true"` and `data-overlay-handles-outside="true"`;
 * those are the documented opt-outs `Overlay` looks for
 * (src/components/ui/Overlay/Overlay.tsx), so no capture-phase listener wrestling
 * is needed.
 *
 * @param onCloseAttempt runs on Escape or on a click outside the ref element.
 * @param enabled set to false while a modal of your own is open, otherwise the
 *   click inside that modal counts as "outside" and closes the overlay.
 */
export const useOverlayCloseGuard = <T extends HTMLElement = HTMLDivElement>(
  onCloseAttempt: () => void,
  enabled = true,
) => {
  const containerRef = useRef<T>(null);
  // Kept in refs so the document listeners can stay registered once and still
  // see current values — re-subscribing on every render drops events.
  const onCloseAttemptRef = useRef(onCloseAttempt);
  const enabledRef = useRef(enabled);
  onCloseAttemptRef.current = onCloseAttempt;
  enabledRef.current = enabled;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!enabledRef.current || event.key !== 'Escape') return;
      // Someone closer to the user already used this Escape — react-select calls
      // preventDefault() when it closes an open menu. Asking "discard your
      // changes?" because a dropdown was dismissed would be plain wrong.
      if (event.defaultPrevented) return;
      event.preventDefault();
      onCloseAttemptRef.current();
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (!enabledRef.current) return;
      const container = containerRef.current;
      if (container && !container.contains(event.target as Node)) onCloseAttemptRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return containerRef;
};
